/* ==========================================================================
   Scroll reveal animations — redjumla
   --------------------------------------------------------------------------
   Reveals INNER CONTENT as it enters the viewport (not whole section boxes).
   For each section it structurally finds the content items and staggers rows
   of cards/columns so they cascade 1,2,3. Repeats on scroll down and up.
   Only opacity + transform are touched (see scroll-animations.css).

   Sections are left ALONE when they: are the hero (already carry explicit
   data-anim), contain a <video> or a position:sticky element (their own
   scroll/video effects), or carry data-anim-skip.

   Signals html.anim-on. If this file fails to load, the inline gate in
   theme.liquid removes anim-on after a timeout so content can't stay hidden.
   ========================================================================== */
(function () {
  'use strict';

  window.__scrollAnimReady = true;

  var root = document.documentElement;
  // Cancel the inline fail-open and guarantee the animation class is present,
  // so a slow/deferred script load can never leave everything un-hidden.
  if (window.__animFailTimer) { clearTimeout(window.__animFailTimer); window.__animFailTimer = null; }
  root.classList.add('anim-on');

  var reduce = window.matchMedia &&
               window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!('IntersectionObserver' in window) || reduce) {
    root.classList.remove('anim-on');
    return;
  }

  var STEP = 0.05;       // small gap so content appears promptly (still cascades)
  var MAX_ORDER = 5;     // tight cap: content-heavy sections reveal fast (<=0.25s)
  var SKIP_TAGS = /^(STYLE|SCRIPT|LINK|TEMPLATE|BR|NOSCRIPT)$/;

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (e.isIntersecting) e.target.classList.add('anim-in');   // reveal
      else e.target.classList.remove('anim-in');                 // reset -> replays on re-entry
    }
  }, {
    threshold: 0,
    rootMargin: '0px 0px -15% 0px' // wait until it's ~15% INTO view before firing,
                                   // so you actually see it animate (repeats both ways)
  });

  var seen = (typeof WeakSet === 'function') ? new WeakSet() : null;
  function observe(el) {
    if (!el) return;
    if (seen) { if (seen.has(el)) return; seen.add(el); }
    observer.observe(el);
  }

  /* ---- Structural target finder ----------------------------------------- */

  // Children that participate in layout (ignore code tags, hidden nodes and
  // absolutely-positioned decorations / overlays / background media).
  function realKids(el) {
    var out = [];
    var kids = el.children;
    for (var i = 0; i < kids.length; i++) {
      var c = kids[i];
      if (SKIP_TAGS.test(c.tagName)) continue;
      var cs = getComputedStyle(c);
      if (cs.display === 'none') continue;
      if (cs.position === 'absolute' || cs.position === 'fixed') continue;
      out.push(c);
    }
    return out;
  }

  // A "row" is a flex/grid container, or any element whose children repeat the
  // same class (a set of cards / columns / items).
  function isRow(el) {
    var k = realKids(el);
    if (k.length < 2) return false;
    var d = getComputedStyle(el).display;
    if (d.indexOf('flex') > -1 || d.indexOf('grid') > -1) return true;
    var c0 = String(k[0].className);
    if (!c0) return false;
    var same = 0;
    for (var i = 0; i < k.length; i++) if (String(k[i].className) === c0) same++;
    return same >= 2;
  }

  // Descend through single-child wrappers to the first branching node.
  function findBranch(node) {
    for (var g = 0; g < 25; g++) {
      var k = realKids(node);
      if (k.length === 1) { node = k[0]; continue; }
      return node;
    }
    return node;
  }

  // Returns [{el, card}] in DOM order. card=true means the item is one of a
  // row of cards/columns (gets the "pop" animation); false = standalone block.
  // A position:sticky element must NOT be transformed (it breaks stickiness) —
  // animate its children instead so sticky sidebars (e.g. FAQ layouts) work.
  function expandSticky(items) {
    var res = [];
    for (var i = 0; i < items.length; i++) {
      var el = items[i].el;
      if (el && getComputedStyle(el).position === 'sticky') {
        var kids = realKids(el);
        if (kids.length) {
          for (var j = 0; j < kids.length; j++) res.push({ el: kids[j], card: items[i].card });
          continue;
        }
      }
      res.push(items[i]);
    }
    return res;
  }

  function collectTargets(section) {
    var branch = findBranch(section);
    var kids = realKids(branch);
    if (kids.length === 0) return expandSticky([{ el: branch, card: false }]);

    var out = [];
    // Branch itself is the row of repeating items -> they're all cards.
    if (isRow(branch)) {
      for (var r = 0; r < kids.length; r++) out.push({ el: kids[r], card: true });
      return expandSticky(out);
    }
    // Otherwise: standalone items pass through; a child that is a row expands
    // into its own children (one level) so the cards inside cascade.
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (isRow(k)) {
        var gk = realKids(k);
        for (var j = 0; j < gk.length; j++) out.push({ el: gk[j], card: true });
      } else {
        out.push({ el: k, card: false });
      }
    }
    return expandSticky(out);
  }

  function sectionProtected(sec) {
    if (sec.querySelector('[data-anim-skip]') || sec.hasAttribute('data-anim-skip')) return true;
    // Only leave PINNED scroll stages alone: a section much taller than the
    // viewport that is built around a sticky pin (stacked media, sticky
    // timeline). Ordinary sticky sidebars and background videos still animate.
    var vh = window.innerHeight || 800;
    if (sec.getBoundingClientRect().height > 1.8 * vh) {
      var all = sec.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        if (getComputedStyle(all[i]).position === 'sticky') return true;
      }
    }
    return false;
  }

  /* ---- Wiring ----------------------------------------------------------- */

  function applyOverrides(el) {
    var delay = el.getAttribute('data-anim-delay');
    if (delay !== null) el.style.setProperty('--anim-delay', parseFloat(delay) + 's');
    var dur = el.getAttribute('data-anim-duration');
    if (dur !== null) el.style.setProperty('--anim-duration', parseFloat(dur) + 's');
  }

  // Explicit [data-anim-stagger] container (e.g. the hero text block).
  function prepStagger(container) {
    var step = parseFloat(container.getAttribute('data-anim-stagger')) || STEP;
    var base = parseFloat(container.getAttribute('data-anim-delay')) || 0;
    var kids = container.children, idx = 0;
    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      if (SKIP_TAGS.test(kid.tagName)) continue;
      if (!kid.hasAttribute('data-anim')) kid.setAttribute('data-anim', 'fade-up');
      kid.style.setProperty('--anim-delay', (base + idx * step) + 's');
      idx++;
    }
  }

  function setup(scope) {
    scope = scope || document;

    // 1) Explicit hero staggers + explicit [data-anim] (server-rendered hidden,
    //    so no race — observe directly).
    var staggers = scope.querySelectorAll('[data-anim-stagger]');
    for (var s = 0; s < staggers.length; s++) prepStagger(staggers[s]);

    var explicit = scope.querySelectorAll('[data-anim]');
    for (var f = 0; f < explicit.length; f++) {
      applyOverrides(explicit[f]);
      observe(explicit[f]);
    }

    // 2) Auto-target inner content of every eligible section — main content
    //    AND the footer group (never the header nav).
    var sections = [];
    var main = document.getElementById('MainContent');
    if (main) {
      for (var mi = 0; mi < main.children.length; mi++) sections.push(main.children[mi]);
    }
    // Footer-group sections carry the group class directly (they ARE the
    // sections, not a wrapper), so collect the elements themselves.
    var footerSecs = document.querySelectorAll('.shopify-section-group-footer-group');
    for (var fi = 0; fi < footerSecs.length; fi++) sections.push(footerSecs[fi]);
    var pending = [];

    for (var n = 0; n < sections.length; n++) {
      var sec = sections[n];
      if (!sec.classList || !sec.classList.contains('shopify-section')) continue;
      if (sec.querySelector('[data-anim]')) continue; // hero / already handled
      if (sectionProtected(sec)) continue;            // pinned scroll / opted-out

      var targets = collectTargets(sec);
      var idx = 0; // running index across the whole section -> rows cascade
                   // sequentially (row1: 1,2,3 then row2: 4,5,6 ...)
      for (var t = 0; t < targets.length; t++) {
        var el = targets[t].el;
        if (!el || el.hasAttribute('data-anim')) continue;
        el.classList.add('anim-init');                          // no transition yet
        el.setAttribute('data-anim', 'fade-up');                // gentle fade + drift
        el.style.setProperty('--anim-delay', (Math.min(idx, MAX_ORDER) * STEP) + 's');
        idx++;
        pending.push(el);
      }
    }

    // Enable transitions on the next frames, then observe (reveal animates).
    var reveal = function () {
      for (var i = 0; i < pending.length; i++) pending[i].classList.remove('anim-init');
      for (var j = 0; j < pending.length; j++) observe(pending[j]);
    };
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { requestAnimationFrame(reveal); });
    }
    // Fallback: rAF is frozen in background tabs, so guarantee the reveal runs.
    setTimeout(reveal, 150); // reveal() is idempotent (observe() dedupes)
  }

  function boot() { setup(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor: re-scan when a section is added / re-rendered.
  document.addEventListener('shopify:section:load', function (evt) {
    setup(evt.target || document);
  });
})();
